/**
 * Feature Flag Configuration
 *
 * Each feature has:
 * - enabled: Boolean flag (reads from environment variables)
 * - name: Human-readable name
 * - description: What the feature does
 * - impacts: Where in the UI this feature affects
 * - reason: Why it's disabled (if applicable)
 */

export const FEATURES = {
  BOOKMARK_SYSTEM: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_BOOKMARK_SYSTEM === 'true',
    name: 'Bookmark System',
    description: 'Allows users to save/bookmark videos to their watchlist',
    impacts: [
      'Save button in VideoDiscoveryCard / VideoCardContainer',
      'Save button in ShortPlayer',
      'Profile "Saved" tab',
      'Watchlist page',
      'POST /api/watchlist/{video:uuid}/toggle API calls',
      'GET /api/watchlist API calls',
    ],
    reason: '',
  },
  WATCH_HISTORY: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_WATCH_HISTORY === 'true',
    name: 'Watch History',
    description: 'Allows users to view their video watch history',
    impacts: [
      'Profile page History tab',
      'History navigation item in sidebar',
      'GET /profile/watch-history API endpoint',
      'useHistoryVideos() query hook',
    ],
    reason: 'Backend not ready - history tracking under development',
  },
  STUDIO_ANALYTICS: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_ANALYTICS === 'true',
    name: 'Studio Analytics',
    description: 'Analytics page in Creator Studio',
    impacts: ['Studio sidebar Analytics link', '/studio/analytics page'],
    reason: 'Page hidden during development',
  },
  STUDIO_PAYOUTS: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_PAYOUTS === 'true',
    name: 'Studio Payouts',
    description: 'Payouts page in Creator Studio',
    impacts: ['Studio sidebar Payouts link', '/studio/payouts page'],
    reason: 'Page hidden during development',
  },
  STUDIO_SETTINGS: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_SETTINGS === 'true',
    name: 'Studio Settings',
    description: 'Settings page in Creator Studio',
    impacts: ['Studio sidebar Settings link', '/studio/settings page'],
    reason: 'Page hidden during development',
  },
  STUDIO_CODES: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_CODES === 'true',
    name: 'Studio Codes',
    description: 'Redeem codes management page in Creator Studio',
    impacts: ['Studio sidebar Codes link', '/studio/codes page'],
    reason: 'Page hidden during development',
  },
  STUDIO_BULK_SELECT: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_BULK_SELECT === 'true',
    name: 'Studio Bulk Select',
    description: 'Row checkboxes + selection toolbar in /studio/content table',
    impacts: [
      'Header checkbox column in ContentTable',
      'Per-row checkbox in StudioContentRow',
      'SelectionToolbar (bulk delete/visibility actions, currently disabled)',
      '"Bulk-edit videos" article in the studio help drawer',
    ],
    reason: 'Backend bulk endpoints not ready',
  },
  STUDIO_PAGE_UPLOAD_BUTTON: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_PAGE_UPLOAD_BUTTON === 'true',
    name: 'Studio Page Upload Button',
    description: 'Legacy page-level Upload button on /studio/content',
    impacts: ['Upload button next to "Channel content" title in PageHeader actions'],
    reason:
      'Hidden during UX test — uploads now flow through "+ Create → Upload video" in StudioHeader',
  },
  EDIT_POST: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_EDIT_POST === 'true',
    name: 'Edit Post',
    description: 'Allows creators to edit posts from the Studio posts table',
    impacts: [
      'Posts tab in ContentTabs (studio content/posts pages)',
      'Pencil edit link in PostsTableRow',
      'MessageSquare comments link in PostsTableRow',
    ],
    reason: 'Edit post functionality disabled during development',
  },
  INVITE_SYSTEM: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_INVITE_SYSTEM === 'true',
    name: 'Invite System',
    description: 'Allows paid subscribers to generate and share invite codes',
    impacts: [
      'Account sidebar "Invite a Friend" tab',
      '/account/invite page',
      'GET /redeem-codes/my-invite API calls',
      'POST /redeem-codes/create-invite API calls',
    ],
    reason: '',
  },
  STUDIO_COURSES: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_COURSES === 'true',
    name: 'Studio Courses',
    description:
      'Creator Studio Courses surface: list page, create modal, detail/videos/details sub-pages, batch-attach picker, and PlaylistsPicker inside the upload/edit modal. Flag-OFF hides every user-clickable entry; the /studio/courses routes still respond to direct URL (creator-only space, low risk during internal release).',
    impacts: [
      'Courses tab in ContentTabs (studio nav)',
      'PlaylistsPicker section inside UploadModal ContentDetailsStep (both upload + edit modes)',
      'Edit-details menu item routing back to /studio/content?editUuid=...',
    ],
    reason:
      'Internal release only — enabled for the single creator uploading first course; broader release after UI polish pass.',
  },
  STUDIO_SERIES: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_STUDIO_SERIES === 'true',
    name: 'Studio Series',
    description:
      'Creator Studio Series (playlists) surface: list page, create modal, detail/episodes sub-pages, and the series group inside the upload/edit picker. Same flag-OFF semantics as STUDIO_COURSES — clickable entries hidden, /studio/series routes still answer direct URLs.',
    impacts: [
      'Series tab in ContentTabs (studio nav)',
      '"New series" item in the global Create menu',
      'Series group inside the collection picker in UploadModal / edit modal',
    ],
    reason: 'FRONT-376 — internal release behind flag until the studio flow is QA-ed end to end.',
  },
  VIDEO_SPLIT_ENDPOINTS_ENABLED: {
    enabled: process.env.NEXT_PUBLIC_FEATURE_VIDEO_SPLIT_ENDPOINTS === 'true',
    name: 'Video Split Endpoints',
    description:
      'Routes /videos/[id] through the parallel /metadata + /view + /recommended + /me/video-state triplet instead of the single /play endpoint. OFF preserves current /play behavior exactly.',
    impacts: [
      'src/app/(platform)/videos/[id]/page.tsx — SSR fan-out branch',
      'src/app/(platform)/videos/[id]/_components/video-player-section.tsx — POST /view on first playing event',
      'GET /videos/{uuid}/metadata (no view write)',
      'POST /videos/{uuid}/view (single view write)',
      'GET /videos/{uuid}/recommended',
      'GET /me/video-state/{uuid}',
      'Public triplet for guests',
    ],
    reason: 'BE endpoints in flight — see FRONT-121 / FRONT-127. Default OFF until BE merges.',
  },
} as const;

export type FeatureName = keyof typeof FEATURES;
export type FeatureConfig = (typeof FEATURES)[FeatureName];

/**
 * isFeatureEnabled
 *
 * Check if a feature is enabled in Server Components or API routes.
 * Use this instead of useFeature() in server-side code.
 */
export function isFeatureEnabled(featureName: FeatureName): boolean {
  return FEATURES[featureName].enabled;
}

/**
 * getFeatureConfig
 *
 * Get the full feature configuration in server-side code.
 */
export function getFeatureConfig(featureName: FeatureName) {
  return FEATURES[featureName];
}

/**
 * getAllFeatures
 *
 * Get all feature flags and their status.
 * Useful for debugging or admin dashboards.
 */
export function getAllFeatures() {
  return Object.entries(FEATURES).map(([key, config]) => ({
    key,
    ...config,
  }));
}
